import * as dates from "date-arithmetic";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { Navigate } from "react-big-calendar";
import TimeGrid from "react-big-calendar/lib/TimeGrid";
import { DefaultDurationValueMap } from "../../types/account/enum";
import { usePreference } from "../../hooks/preference/usePreference";
import { DefaultPreferenceOptions } from "../../constants/userPreference";
import { DEFAULT_DISPLAY_TIME_SLOT_COUNT } from "../../constants/calendar";

export default function CustomDay({
  date,
  localizer,
  max = localizer.endOf(new Date(), "day"),
  min = localizer.startOf(new Date(), "day"),
  scrollToTime = localizer.startOf(new Date(), "day"),
  ...props
}) {
  const { preference: userPreference } = usePreference();
  const isInitializeScroll = useRef(false);

  const currRange = useMemo(
    () => CustomDay.range(localizer.startOf(date, "day"), { localizer }),
    [date, localizer]
  );
  const defaultDuration =
    DefaultDurationValueMap[
      userPreference?.defaultDuration ?? DefaultPreferenceOptions.defaultDuration
    ];

  useEffect(
    function initializeScroll() {
      // NOTE: 커스텀뷰에서 scrollToTime이 적용되지 않아 초기 스크롤 로직 처리
      if (isInitializeScroll.current) return;

      const wrapper = document.querySelector(".rbc-time-content");
      const element = document.querySelector(`.rbc-current-time-indicator`);
      if (!wrapper || !element) {
        return;
      }

      const now = new Date();
      const DISPLAY_TIME_SLOT_COUNT = DEFAULT_DISPLAY_TIME_SLOT_COUNT;
      const TIME_SLOT_HEIGHT = 80;
      const MINUTES_TO_PX = (TIME_SLOT_HEIGHT / 60) * now.getMinutes();
      const OFFSET_TOP = 34; // 기존 react-big-calendar의 offsetTop 반영

      const scrollPosition =
        TIME_SLOT_HEIGHT * DISPLAY_TIME_SLOT_COUNT + MINUTES_TO_PX - OFFSET_TOP;
      wrapper.scrollTo({
        top: element.offsetTop - scrollPosition,
      });

      isInitializeScroll.current = true;
    },
    [scrollToTime]
  );

  return (
    <>
      <div className="custom-view-day"></div>
      <TimeGrid
        date={date}
        eventOffset={defaultDuration}
        localizer={localizer}
        max={max}
        min={min}
        range={currRange}
        scrollToTime={scrollToTime}
        {...props}
      />
    </>
  );
}

CustomDay.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  localizer: PropTypes.object,
  max: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
};

CustomDay.range = (date, { localizer }) => {
  const start = date;
  const end = dates.add(start, 2, "day");

  let current = start;
  const range = [];

  while (localizer.lte(current, end, "day")) {
    range.push(current);
    current = localizer.add(current, 1, "day");
  }

  return range;
};

CustomDay.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -3, "day");

    case Navigate.NEXT:
      return localizer.add(date, 3, "day");

    default:
      return date;
  }
};

CustomDay.title = (date) => {
  return `My Custom day: ${date.toLocaleDateString()}`;
};
